import { useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import { PlaceMarker } from './PlaceMarker';
import { MapLegend } from './MapLegend';
import { usePlaces } from '../hooks/usePlaces';
import { useMapTheme } from '../hooks/useMapTheme';
import { DURATION_DRAMATIC } from '../utils/animationTiming';
import { getTheme } from '../config/theme.config';
import type { AnimationState } from '../hooks/useAnimation';
import type { MarkerState, Place } from '../types/Place';
import type { MapTheme } from '@love-map/shared';

// Story 8-10: Custom cluster icon creator for dark theme styling
// Epic 9: Extended to show glow when cluster contains highlighted marker
interface ClusterMarker extends L.Marker {
  getLatLng: () => L.LatLng;
}

interface Cluster {
  getChildCount: () => number;
  getAllChildMarkers: () => ClusterMarker[];
}

const createClusterIconFactory = (
  highlightedCoords: [number, number] | null
) => {
  return (cluster: Cluster) => {
    const count = cluster.getChildCount();
    let size = 'small';
    if (count >= 10) size = 'medium';
    if (count >= 20) size = 'large';

    // Check if cluster contains the highlighted marker
    let containsHighlighted = false;
    if (highlightedCoords) {
      const childMarkers = cluster.getAllChildMarkers();
      containsHighlighted = childMarkers.some(marker => {
        const pos = marker.getLatLng();
        return (
          Math.abs(pos.lat - highlightedCoords[0]) < 0.0001 &&
          Math.abs(pos.lng - highlightedCoords[1]) < 0.0001
        );
      });
    }

    const highlightedClass = containsHighlighted ? 'cluster-highlighted' : '';

    return L.divIcon({
      html: `<div class="cluster-marker cluster-${size} ${highlightedClass}"><span>${count}</span></div>`,
      className: 'custom-marker-cluster',
      iconSize: L.point(40, 40, true),
    });
  };
};

// Map view settings sourced from theme.config.ts (single source of truth)
const themeMap = getTheme().map;

// Note: Tile URL and attribution are now managed by useMapTheme hook (SPRINT-5.5)
// to support dynamic map styles (dark, light, satellite, terrain)

interface MapViewProps {
  className?: string;
  onMarkerClick?: (id: string) => void;
  onMapReady?: () => void;
  animationState?: AnimationState;
  visiblePlaces?: Set<string>;
  getMarkerState?: (placeId: string) => MarkerState;
  targetPlaceId?: string | null;
  onCenterComplete?: () => void;
  onMapInteraction?: () => void; // M5: Called when user interacts with map
  activeLayer?: string; // Story 8-2: Track layer for auto-center on filter change
  resetViewTrigger?: number; // Story 8-5: Force map recenter when logo clicked
  highlightedPlaceId?: string | null; // Epic 9: For cluster glow when containing highlighted marker
  isTimeMachineMode?: boolean; // Epic 9: To adjust legend position when timeline is visible
  mapTheme?: MapTheme; // SPRINT-5.5: Custom map theme settings
}

// Component to detect when map tiles are loaded
function MapReadyHandler({ onMapReady }: { onMapReady?: () => void }) {
  const map = useMap();
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (!map || hasFiredRef.current) return;

    let invalidateSizeTimerId: number | null = null;

    const handleLoad = () => {
      if (!hasFiredRef.current) {
        hasFiredRef.current = true;
        // Fix for black tiles on mobile (especially iPhone): invalidateSize forces
        // Leaflet to recalculate container dimensions after CSS has fully resolved
        invalidateSizeTimerId = window.setTimeout(() => {
          map.invalidateSize();
        }, 100);
        onMapReady?.();
      }
    };

    // Listen for whenReady which fires after tiles are loaded
    map.whenReady(handleLoad);

    return () => {
      if (invalidateSizeTimerId !== null) {
        clearTimeout(invalidateSizeTimerId);
      }
      map.off('load', handleLoad);
    };
  }, [map, onMapReady]);

  return null;
}

// Component to handle programmatic map centering
function MapCenterController({
  targetPlaceId,
  places,
  onCenterComplete,
}: {
  targetPlaceId: string | null;
  places: Place[];
  onCenterComplete?: () => void;
}) {
  const map = useMap();
  const previousTargetRef = useRef<string | null>(null);

  // H4: Reset previousTargetRef when targetPlaceId becomes null
  // This allows re-centering on the same place after user pans away
  useEffect(() => {
    if (targetPlaceId === null) {
      previousTargetRef.current = null;
    }
  }, [targetPlaceId]);

  useEffect(() => {
    // Only fly when targetPlaceId changes to a new value
    if (targetPlaceId && targetPlaceId !== previousTargetRef.current) {
      const place = places.find((p) => p.id === targetPlaceId);
      if (place) {
        // Defensive check: flyTo may not exist in test mocks.
        // Proper test setup would mock Leaflet, but this guard prevents test failures.
        if (typeof map.flyTo === 'function') {
          map.flyTo(place.coordinates, 10, {
            duration: DURATION_DRAMATIC / 1000, // Use animation token (500ms)
          });

          // Callback when animation completes
          const handleMoveEnd = () => {
            map.off('moveend', handleMoveEnd);
            onCenterComplete?.();
          };
          map.on('moveend', handleMoveEnd);
        } else {
          // Fallback for environments where flyTo is not available
          onCenterComplete?.();
        }
      }
      previousTargetRef.current = targetPlaceId;
    }
  }, [targetPlaceId, places, map, onCenterComplete]);

  return null;
}

// M5: Component to detect user map interactions (drag, click)
function MapInteractionHandler({ onInteraction }: { onInteraction?: () => void }) {
  const map = useMap();

  useEffect(() => {
    if (!onInteraction) return;

    const handleInteraction = () => {
      onInteraction();
    };

    // Listen for user-initiated interactions
    map.on('dragstart', handleInteraction);
    map.on('zoomstart', handleInteraction);

    return () => {
      map.off('dragstart', handleInteraction);
      map.off('zoomstart', handleInteraction);
    };
  }, [map, onInteraction]);

  return null;
}

// Story 8-2: Component to auto-center map when layer filter changes
function MapBoundsController({
  visiblePlaces,
  places,
  activeLayer,
  animationState,
  resetViewTrigger,
}: {
  visiblePlaces?: Set<string>;
  places: Place[];
  activeLayer?: string;
  animationState?: AnimationState;
  resetViewTrigger?: number;
}) {
  const map = useMap();
  const previousLayerRef = useRef<string | undefined>(activeLayer);
  const previousResetTriggerRef = useRef<number | undefined>(resetViewTrigger);
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    // Skip on initial mount and during animation
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      previousLayerRef.current = activeLayer;
      previousResetTriggerRef.current = resetViewTrigger;
      return;
    }

    // Check if we should recenter (either layer changed or reset triggered)
    const layerChanged = activeLayer !== previousLayerRef.current;
    const resetTriggered = resetViewTrigger !== previousResetTriggerRef.current;

    if (!layerChanged && !resetTriggered) {
      return;
    }

    // Skip during animation
    if (animationState !== 'complete') {
      return;
    }

    previousLayerRef.current = activeLayer;
    previousResetTriggerRef.current = resetViewTrigger;

    // Get visible places for the current filter
    const visiblePlacesList = places.filter(
      p => !visiblePlaces || visiblePlaces.has(p.id)
    );

    // If no visible places, don't change the view
    if (visiblePlacesList.length === 0) {
      return;
    }

    // Calculate bounds from visible places
    const bounds = L.latLngBounds(
      visiblePlacesList.map(p => L.latLng(p.coordinates[0], p.coordinates[1]))
    );

    // Fit bounds with padding and animation
    if (typeof map.flyToBounds === 'function') {
      map.flyToBounds(bounds, {
        padding: [50, 50],
        duration: DURATION_DRAMATIC / 1000,
        maxZoom: 10, // Don't zoom in too much for single markers
      });
    } else if (typeof map.fitBounds === 'function') {
      // Fallback for test environments
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
    }
  }, [activeLayer, visiblePlaces, places, map, animationState, resetViewTrigger]);

  return null;
}

export function MapView({
  className,
  onMarkerClick,
  onMapReady,
  animationState = 'complete',
  visiblePlaces,
  getMarkerState,
  targetPlaceId,
  onCenterComplete,
  onMapInteraction,
  activeLayer,
  resetViewTrigger,
  highlightedPlaceId,
  isTimeMachineMode = false,
  mapTheme,
}: MapViewProps) {
  const { places } = usePlaces();

  // SPRINT-5.5: Get theme-specific tile URL and CSS variables
  const { tileUrl, tileAttribution, cssVariables, isLightTheme } = useMapTheme(mapTheme);

  const handleMarkerClick = (id: string) => {
    onMarkerClick?.(id);
  };

  // Markers only interactive after animation completes (AC3, AC4)
  const isInteractive = animationState === 'complete';

  // Epic 9: Pre-compute highlighted place coordinates for cluster icon function
  const highlightedCoords = useMemo(() => {
    if (!highlightedPlaceId) return null;
    const place = places.find(p => p.id === highlightedPlaceId);
    return place ? place.coordinates : null;
  }, [places, highlightedPlaceId]);

  // Epic 9: Memoized cluster icon function with highlighted marker awareness
  const clusterIconFunction = useMemo(
    () => createClusterIconFactory(highlightedCoords),
    [highlightedCoords]
  );

  // Check if filter results in empty visible places (Story 5.3 empty state)
  const isFilteredEmpty = visiblePlaces !== undefined && visiblePlaces.size === 0 && animationState === 'complete';

  // Apply theme CSS variables as inline style
  const themeStyle = useMemo(() => {
    const style: Record<string, string> = {};
    Object.entries(cssVariables).forEach(([key, value]) => {
      style[key] = value;
    });
    return style;
  }, [cssVariables]);

  return (
    <div
      className={className ?? 'h-full w-full relative'}
      role="application"
      aria-label="Interactive travel map"
      style={themeStyle}
    >
      <MapContainer
        center={themeMap.defaultCenter}
        zoom={themeMap.defaultZoom}
        minZoom={themeMap.minZoom}
        maxZoom={themeMap.maxZoom}
        scrollWheelZoom={true}
        touchZoom={true}
        dragging={true}
        className={`h-full w-full ${isLightTheme ? 'bg-gray-200' : 'bg-bg-base'}`}
      >
        <MapReadyHandler onMapReady={onMapReady} />
        <MapCenterController
          targetPlaceId={targetPlaceId ?? null}
          places={places}
          onCenterComplete={onCenterComplete}
        />
        <MapInteractionHandler onInteraction={onMapInteraction} />
        <MapBoundsController
          visiblePlaces={visiblePlaces}
          places={places}
          activeLayer={activeLayer}
          animationState={animationState}
          resetViewTrigger={resetViewTrigger}
        />
        {/* SPRINT-5.5: Use dynamic tile URL based on theme */}
        <TileLayer
          url={tileUrl}
          attribution={tileAttribution}
        />
        {/* Story 8-10: Cluster markers for dense areas */}
        {/* Epic 9: Uses clusterIconFunction for highlighted marker glow */}
        <MarkerClusterGroup
          chunkedLoading
          iconCreateFunction={clusterIconFunction}
          maxClusterRadius={themeMap.clusterMaxRadius}
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={false}
          zoomToBoundsOnClick={true}
          disableClusteringAtZoom={themeMap.clusterDisableAtZoom}
        >
          {places.map((place) => {
            const isVisible = !visiblePlaces || visiblePlaces.has(place.id);
            const markerState = getMarkerState ? getMarkerState(place.id) : 'default';
            return (
              <PlaceMarker
                key={place.id}
                place={place}
                onClick={handleMarkerClick}
                isVisible={isVisible}
                isInteractive={isInteractive}
                state={markerState}
              />
            );
          })}
        </MarkerClusterGroup>
      </MapContainer>

      {/* Story 8-7: Marker color legend */}
      {animationState === 'complete' && <MapLegend isTimeMachineMode={isTimeMachineMode} />}

      {/* Empty state overlay when filter returns no results */}
      {isFilteredEmpty && (
        <div
          className="absolute bottom-20 left-1/2 -translate-x-1/2 z-[1000] bg-bg-surface/90 backdrop-blur-sm px-4 py-3 rounded-lg border border-gray-700 shadow-lg"
          data-testid="map-empty-state"
          role="status"
          aria-live="polite"
        >
          <p className="text-text-secondary text-sm text-center">
            No places in this category
          </p>
        </div>
      )}
    </div>
  );
}
